import { isValidPhoneNumber } from 'libphonenumber-js';

import { defaultCountryCode } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';
import {
  AddMemberFormDataType,
  AddMemberModalType,
  MemberListType
} from '@types';
import {
  isInvalidAmount,
  isInvalidEmail,
  isInvalidMemberName,
  isInvalidPincode
} from '@utils/validations';

export const hasChangesInMemberDetails = (
  formData: AddMemberFormDataType,
  selectedmember?: MemberListType | null
) => {
  const {
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    amount,
    dueDate,
    startDate,
    email,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    pincode,
    termsList,
    endDate,
    totalFee
  } = formData;
  return (
    selectedmember?.name !== name ||
    selectedmember?.primaryNumber !== primaryNumber ||
    selectedmember.altNumber !== altNumber ||
    selectedmember.amount !== amount ||
    selectedmember.dueDate !== dueDate.day ||
    selectedmember.interval !== dueDate.interval ||
    selectedmember.paymentCollectionType !== dueDate.paymentCollectionType ||
    selectedmember.startDate !== startDate ||
    selectedmember.altNumberCountryCode !== altCountryCode ||
    selectedmember.primaryNumberCountryCode !== primaryCountryCode ||
    selectedmember.email !== email ||
    selectedmember.addressLine1 !== addressLine1 ||
    selectedmember.addressLine2 !== addressLine2 ||
    selectedmember.country !== country ||
    selectedmember.state !== state ||
    selectedmember.city !== city ||
    selectedmember.pincode !== pincode ||
    selectedmember.endDate !== endDate ||
    selectedmember.totalFee !== totalFee ||
    selectedmember.termsList?.some(
      (term, index) =>
        termsList[index].dueAmount !== term.dueAmount ||
        termsList[index].dueDate !== term.dueDate ||
        termsList[index].feeCategoryData?.some(
          (category, catIndex) =>
            category.amount !== term.feeCategoryData?.[catIndex].amount
        )
    )
  );
};

export const isMemberSaveDisabled = (
  formData: AddMemberFormDataType,
  groupMemberMaxAmount: string,
  groupMemberMinAmount: string,
  memberNameMaxLength: string,
  memberNameMinLength: string,
  supported_phone_number_countries: any,
  isFeeCategoryEnabled: boolean
) => {
  const {
    amount,
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    feeCategories,
    email,
    pincode,
    termsList,
    totalFee,
    endDate,
    startDate
  } = formData;

  const getCountryId = (countryCode: string) =>
    (supported_phone_number_countries || []).find(
      (countryData: CountryData) => `${countryData.dial_code}` === countryCode
    )?.code;
  const areAllFeeCategoriesFilled =
    feeCategories.length > 0 &&
    feeCategories.every(
      (category: any) => category.value !== undefined && category.value !== ''
    );

  // Check if amount is invalid
  const isAmountInvalid =
    termsList?.length > 0
      ? (amount && Number(amount) < Number(groupMemberMinAmount)) ||
        termsList.some(
          (term) =>
            term.dueAmount &&
            term.dueAmount !== '0' &&
            isInvalidAmount(
              term.dueAmount,
              groupMemberMaxAmount,
              groupMemberMinAmount
            )
        )
      : amount &&
        isInvalidAmount(amount, groupMemberMaxAmount, groupMemberMinAmount);

  return (
    isAmountInvalid ||
    isInvalidMemberName(name, memberNameMaxLength, memberNameMinLength) ||
    !isValidPhoneNumber(primaryNumber, getCountryId(primaryCountryCode)) ||
    !!(
      altNumber && !isValidPhoneNumber(altNumber, getCountryId(altCountryCode))
    ) ||
    `${altCountryCode}_${altNumber}` ===
      `${primaryCountryCode}_${primaryNumber}` ||
    (feeCategories.length > 0 &&
      isFeeCategoryEnabled &&
      !areAllFeeCategoriesFilled) ||
    !!(email && isInvalidEmail(email)) ||
    !!(pincode && isInvalidPincode(pincode)) ||
    (endDate && new Date(endDate) < new Date(startDate)) ||
    (totalFee && Number(totalFee) < Number(groupMemberMinAmount)) ||
    (totalFee && Number(totalFee) > Number(groupMemberMaxAmount))
  );
};

export const addMemberFormDataInitialState = {
  feeCategories: [],
  termsList: [],
  name: '',
  email: '',
  primaryCountryCode: defaultCountryCode,
  primaryNumber: '',
  altCountryCode: defaultCountryCode,
  altNumber: '',
  startDate: '',
  addressLine1: '',
  addressLine2: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
  totalFee: ''
};

export const getFormattedAddMemberData = (
  formData: AddMemberFormDataType,
  type: AddMemberModalType,
  memberId: string,
  id: string,
  groupAmount?: string,
  groupDueDate?: string
) => {
  const formattedDuesDate = () => {
    const dueDateValue = formData.dueDate.day || groupDueDate || '';
    return dueDateValue;
  };
  return {
    primaryNumber: formData.primaryNumber,
    name: formData.name,
    altNumber: formData.altNumber || '',
    amount: formData.amount || groupAmount || '',
    totalFee: formData.totalFee || '',
    dueDate: formattedDuesDate(),
    paymentCollectionType: formData.dueDate.paymentCollectionType,
    interval: formData.dueDate.interval,
    feeCategoryData: formData.feeCategories,
    id:
      type === AddMemberModalType.EDIT
        ? memberId
        : `member_${id}_${formData.name}${formData.primaryCountryCode}${formData.primaryNumber}`,
    primaryNumberCountryCode: formData.primaryCountryCode,
    altNumberCountryCode: formData.altCountryCode,
    startDate: formData.startDate,
    endDate: formData.endDate,
    email: formData.email,
    addressLine1: formData.addressLine1,
    addressLine2: formData.addressLine2,
    country: formData.country,
    state: formData.state,
    city: formData.city,
    pincode: formData.pincode,
    termsList: formData.termsList
  };
};
